import { LOTTE_CATEGORY_FIELD } from '@/constants/lotte-api/category';
import { CATEGORY_TYPE_SEARCH_MODE } from '@/enums/category-type';
import { getPoiListImageUrlFromContent } from '@/utils/poi/poiImage';
import type { BuildingInStoreInfo, BuildingMenu, FloorInfo, PoiContent } from '@/services/dto';
import type { CategoryTypeFilterConfig } from '@/stores/types/categoryTypeStore';
import type { PoiContentSlide } from '@/types/shop';
import poiUtils from '@/utils/poi';

export const getCategoryTypeMenu = (
    buildingMenus: Record<string, BuildingMenu[]> | null | undefined,
    language: string,
    mainLanguage: string,
    menuId: string | null
): BuildingMenu | undefined => {
    if (!menuId) return undefined;
    const menus = buildingMenus?.[language] ?? buildingMenus?.[mainLanguage] ?? [];
    return menus.find((menu) => menu.id === menuId);
};

export const resolveCategoryTypePoiContents = (
    menu: BuildingMenu | undefined,
    selectedBuildingId: string | null,
    searchMode: CATEGORY_TYPE_SEARCH_MODE
): PoiContent[] => {
    if (!menu) return [];

    const isFloorModeWithBuilding = searchMode === CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_FLOOR && selectedBuildingId && selectedBuildingId !== 'all';

    if (isFloorModeWithBuilding && menu.poiContentBuildings?.length) {
        const match = menu.poiContentBuildings.find((b) => b.id === selectedBuildingId);
        return match?.poiContents ?? menu.poiContents ?? [];
    }

    return menu.poiContents ?? [];
};

export const convertPoiContentToSlide = (lang: string, buildings: BuildingInStoreInfo[], pois?: PoiContent[], searchText?: string): PoiContentSlide[] => {
    const buildingById = new Map(buildings.map((b) => [b.studioBuildingId, b]));

    const allPois: { poi: PoiContent; building: BuildingInStoreInfo }[] = pois
        ? pois.flatMap((poi) => {
              const building = buildingById.get(poi.studioBuildingId);
              return building ? [{ poi, building }] : [];
          })
        : [];

    return allPois?.map(({ poi, building }) => {
        const formatData = {
            name: poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'],
            shopUrl: getPoiListImageUrlFromContent(poi, lang),
        };

        const name = formatData.name || '';
        const shopUrl = formatData.shopUrl || '';
        const floor = poiUtils.resolveFloorSlideLabel(poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []));
        const buildingName = poiUtils.getBuildingLocalizedName(building, lang);
        const poiCategoryCodes = poiUtils.getPoiCategoryCodes(poi);

        const finalCategory = building.filterCategories.shop.find((f) => poiCategoryCodes.includes(f.categoryCode));

        const poiType = 'normal';

        return {
            id: poi.id,
            name,
            buildingName,
            floor,
            url: shopUrl,
            data: poi,
            poiType,
            category: finalCategory ?? undefined,
        };
    });
};

export const filterByFloor = (item: PoiContentSlide, config: CategoryTypeFilterConfig): boolean => {
    const { selectedBuildingId, selectedFloor, buildings } = config;

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return item.data.studioBuildingId === building.studioBuildingId;
        }

        return item.data.studioFloorId === (selectedFloor?.data as FloorInfo)?.floor_code && item.data.studioBuildingId === building.studioBuildingId;
    }

    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(item.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (item: PoiContentSlide, config: CategoryTypeFilterConfig): boolean => {
    const { selectedCategory, selectedBuildingId, buildings, categoryCodesForFilter } = config;

    if (!selectedCategory) return false;

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (item.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    if (!categoryCodesForFilter?.length) return false;

    const poiCategoryCodes = poiUtils.getPoiCategoryCodes(item.data);
    return categoryCodesForFilter.some((code) => poiCategoryCodes.includes(code));
};

export const filterByText = (item: PoiContentSlide, config: CategoryTypeFilterConfig, keyboardLanguage: 'ko' | 'en'): boolean => {
    const { searchText, language, selectedBuildingId, buildings } = config;

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (item.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (!searchText?.trim()) return true;

    const nameKeyboard = item.data.name[keyboardLanguage] || '';
    const nameDefault = item.data.name[language] || '';
    const normalizedSearchText = searchText.trim();

    return poiUtils.searchByText(nameKeyboard, normalizedSearchText) || poiUtils.searchByText(nameDefault, normalizedSearchText);
};

export const createCategoryTypeFilter = (searchMode: CATEGORY_TYPE_SEARCH_MODE, config: CategoryTypeFilterConfig, keyboardLanguage: 'ko' | 'en') => {
    const filterMap = {
        [CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
        [CATEGORY_TYPE_SEARCH_MODE.SEARCH_BY_TEXT]: filterByText,
    };

    const filterFunction = filterMap[searchMode];

    return (item: PoiContentSlide) => {
        return filterFunction ? filterFunction(item, config, keyboardLanguage) : true;
    };
};

const categoryTypeUtils = {
    convertPoiContentToSlide,
    createCategoryTypeFilter,
    filterByFloor,
    filterByCategory,
    filterByText,
    getCategoryTypeMenu,
    resolveCategoryTypePoiContents,
};

export default categoryTypeUtils;
